/**
 * Call Provider Preference
 * Mirrors the Chrome extension's "Call provider" setting (chrome.storage `selectedCallOption`):
 * 'call' → tel: protocol, 'googlevoice' → Google Voice web dialer, 'ringcentral' → RingCentral web click-to-dial.
 * Persisted in localStorage; two-way synced with the extension (when installed) via external messages
 * so the extension-handled call buttons and the page's native dialing behave identically.
 */

import { EXTENSION_ID } from 'services/factoring/constants'

export type CallProvider = 'call' | 'googlevoice' | 'ringcentral'

export const CALL_PROVIDERS: { value: CallProvider; label: string }[] = [
    { value: 'call', label: 'Call' },
    { value: 'googlevoice', label: 'Google Voice' },
    { value: 'ringcentral', label: 'Ring Central Web' },
]

const STORAGE_KEY = 'private_loads_call_provider'
export const CALL_PROVIDER_CHANGED_EVENT = 'lh:call-provider-changed'

export const isCallProvider = (value: unknown): value is CallProvider => value === 'call' || value === 'googlevoice' || value === 'ringcentral'

export const getCallProviderLabel = (provider: CallProvider): string => CALL_PROVIDERS.find((option) => option.value === provider)?.label ?? 'Call'

const isChromeRuntimeAvailable = (): boolean => typeof chrome !== 'undefined' && !!chrome.runtime && !!chrome.runtime.sendMessage

export const getStoredCallProvider = (): CallProvider => {
    try {
        const stored = localStorage.getItem(STORAGE_KEY)
        return isCallProvider(stored) ? stored : 'call'
    } catch (error) {
        console.error('Failed to read call provider from localStorage:', error)
        return 'call'
    }
}

/** Push the choice into the extension so its call buttons (which own `.rc_call_icon` clicks) use the same provider. */
export const syncCallProviderToExtension = (provider: CallProvider): void => {
    if (!isChromeRuntimeAvailable()) return
    try {
        chrome.runtime.sendMessage(EXTENSION_ID, { type: 'SET_CALL_OPTION', value: provider }, () => {
            // Older extension versions don't handle this message type — lastError is expected there.
            void chrome.runtime.lastError
        })
    } catch (error) {
        console.error('Failed to sync call provider to extension:', error)
    }
}

/** Read the extension's saved `selectedCallOption` so the page starts aligned with it. Null when unavailable. */
export const fetchCallProviderFromExtension = (): Promise<CallProvider | null> =>
    new Promise((resolve) => {
        if (!isChromeRuntimeAvailable()) {
            resolve(null)
            return
        }
        try {
            chrome.runtime.sendMessage(EXTENSION_ID, { type: 'GET_CALL_OPTION' }, (response) => {
                if (chrome.runtime.lastError) {
                    resolve(null)
                    return
                }
                const value = (response as { value?: unknown } | undefined)?.value
                resolve(isCallProvider(value) ? value : null)
            })
        } catch (error) {
            console.error('Failed to read call provider from extension:', error)
            resolve(null)
        }
    })

export const saveCallProvider = (provider: CallProvider): void => {
    try {
        localStorage.setItem(STORAGE_KEY, provider)
    } catch (error) {
        console.error('Failed to persist call provider:', error)
    }
    window.dispatchEvent(new CustomEvent<CallProvider>(CALL_PROVIDER_CHANGED_EVENT, { detail: provider }))
    syncCallProviderToExtension(provider)
}

/**
 * Pull the base number out of the raw formats `brokerContactPhone` arrives in:
 * JSON (`{"number":"...","extension":"..."}`), tel: URI (`tel:+1...;ext=123`), or plain
 * display text with an "x123"/"ext 123" suffix. Phone extensions are dropped — they
 * can't ride along in tel:/Google Voice URLs.
 */
const extractPhoneBase = (phone: string): string => {
    const trimmed = phone.trim()
    if (trimmed.startsWith('{')) {
        try {
            const parsed = JSON.parse(trimmed) as Record<string, unknown>
            const number = parsed.number ?? parsed.phone ?? parsed.value
            if (typeof number === 'string' && number) return number
        } catch {
            // Not valid JSON after all — fall through to the plain-text paths below.
        }
    }
    if (trimmed.toLowerCase().startsWith('tel:')) return trimmed.slice(4).split(';')[0]
    return trimmed.split(/x/i)[0]
}

/**
 * Normalize a display/raw phone into +1XXXXXXXXXX for dialing (US default). Branch order and
 * fallbacks mirror the extension's callUtils exactly — an explicit leading `+` is honored as-is,
 * and any other shape gets `+1` prefixed — so both dial paths always target the same number.
 */
const toDialablePhone = (phone: string): string | null => {
    const base = extractPhoneBase(phone).trim()
    const digits = base.replace(/\D/g, '')
    if (digits.length < 10) return null
    if (base.startsWith('+')) return `+${digits}`
    if (digits.length === 11 && digits.startsWith('1')) return `+${digits}`
    return `+1${digits}`
}

export const buildCallHref = (provider: CallProvider, phone: string): string | null => {
    const dialable = toDialablePhone(phone)
    if (!dialable) return null
    switch (provider) {
        case 'googlevoice':
            return `https://voice.google.com/u/0/calls?a=nc,${dialable}`
        case 'ringcentral':
            return `https://app.ringcentral.com/c2d/?number=${encodeURIComponent(dialable)}`
        case 'call':
        default:
            return `tel:${dialable}`
    }
}

/** Web dialers open in a new tab; tel: hands off to the OS handler in place. */
export const opensInNewTab = (provider: CallProvider): boolean => provider !== 'call'

export const initiateLoadHubCall = (provider: CallProvider, phone: string): boolean => {
    const href = buildCallHref(provider, phone)
    if (!href) return false
    if (opensInNewTab(provider)) {
        window.open(href, '_blank', 'noopener,noreferrer')
    } else {
        window.location.href = href
    }
    return true
}
